import { useMemo } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Plus, Trash2, GripVertical, FileText, Image, Square } from 'lucide-react';
import { PageBackground } from '@/types/quote';
import { cn } from '@/lib/utils';

interface SortablePageItemProps {
  id: string;
  pageNumber: number;
  background: PageBackground | undefined;
  isActive: boolean;
  canDelete: boolean;
  isPdfPage: boolean; // Whether this page is within the PDF background range
  onSelect: () => void;
  onDelete: () => void;
}

const SortablePageItem = ({
  id,
  pageNumber,
  background,
  isActive,
  canDelete,
  isPdfPage,
  onSelect,
  onDelete
}: SortablePageItemProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const getPageIcon = () => {
    // Use isPdfPage to determine if this is a PDF background page
    if (isPdfPage) {
      return <FileText className="h-4 w-4 text-primary" />;
    }
    if (!background) return <Square className="h-4 w-4 text-muted-foreground" />;
    switch (background.type) {
      case 'image':
        return <Image className="h-4 w-4 text-green-500" />;
      case 'blank':
      default:
        return <Square className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getPageLabel = () => {
    // Use isPdfPage to determine the label
    if (isPdfPage) {
      return 'PDF';
    }
    if (!background) return 'Blanco';
    switch (background.type) {
      case 'image':
        return 'Afbeelding';
      case 'blank':
      default:
        return 'Blanco';
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex items-center gap-1.5 p-1.5 rounded border cursor-pointer transition-all',
        isActive ? 'border-primary bg-primary/10' : 'border-border hover:border-primary/50',
        isDragging && 'opacity-50 shadow-lg'
      )}
    >
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing p-0.5 hover:bg-muted rounded"
      >
        <GripVertical className="h-3 w-3 text-muted-foreground" />
      </div>
      
      <div
        className="flex-1 flex items-center gap-1.5"
        onClick={onSelect}
      >
        <div className="w-6 h-8 bg-white border rounded shadow-sm flex items-center justify-center flex-shrink-0">
          {getPageIcon()}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium truncate">P{pageNumber}</p>
          <p className="text-[10px] text-muted-foreground truncate">{getPageLabel()}</p>
        </div>
      </div>
      
      {canDelete && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-5 w-5 text-destructive hover:text-destructive flex-shrink-0"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                }}
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Pagina verwijderen</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
};

interface PageOrderPanelProps {
  pageCount: number;
  pageBackgrounds: PageBackground[];
  currentPage: number;
  pdfPageCount?: number; // Actual number of pages in the PDF background
  onPageSelect: (page: number) => void;
  onReorder: (fromIndex: number, toIndex: number) => void;
  onAddPage: () => void;
  onDeletePage: (page: number) => void;
}

export const PageOrderPanel = ({
  pageCount,
  pageBackgrounds,
  currentPage,
  pdfPageCount = 0,
  onPageSelect,
  onReorder,
  onAddPage,
  onDeletePage
}: PageOrderPanelProps) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Create array of page items
  const pageItems = useMemo(() => {
    return Array.from({ length: pageCount }, (_, i) => ({
      id: `page-${i + 1}`,
      pageNumber: i + 1,
      background: pageBackgrounds.find(bg => bg.page === i + 1),
    }));
  }, [pageCount, pageBackgrounds]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = pageItems.findIndex(item => item.id === active.id);
      const newIndex = pageItems.findIndex(item => item.id === over.id);
      onReorder(oldIndex, newIndex);
    }
  };

  const canDeletePage = (pageNumber: number) => {
    // Allow deletion only for blank pages and if there's more than 1 page
    if (pageCount <= 1) return false;
    const background = pageBackgrounds.find(bg => bg.page === pageNumber);
    return !background || background.type === 'blank';
  };

  return (
    <div className="flex flex-col h-full w-44 border-r bg-background">
      <div className="p-2 border-b">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-xs font-semibold">Pagina's</h3>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-6 text-[10px] px-2"
                  onClick={onAddPage}
                >
                  <Plus className="h-3 w-3 mr-0.5" />
                  Nieuw
                </Button>
              </TooltipTrigger>
              <TooltipContent>Blanco pagina toevoegen</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <p className="text-[10px] text-muted-foreground">
          Sleep om te ordenen
        </p>
      </div>

      <div className="flex-1 overflow-y-auto p-2 space-y-1">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={pageItems.map(item => item.id)}
            strategy={verticalListSortingStrategy}
          >
            {pageItems.map((item) => (
              <SortablePageItem
                key={item.id}
                id={item.id}
                pageNumber={item.pageNumber}
                background={item.background}
                isActive={currentPage === item.pageNumber}
                canDelete={canDeletePage(item.pageNumber)}
                isPdfPage={pdfPageCount > 0 && item.pageNumber <= pdfPageCount}
                onSelect={() => onPageSelect(item.pageNumber)}
                onDelete={() => onDeletePage(item.pageNumber)}
              />
            ))}
          </SortableContext>
        </DndContext>
      </div>
    </div>
  );
};
